import {
  emptySeverityCounts,
  normalizeProjectKey,
  addSeverityCounts,
  compareTriage,
  toSeverityCounts
} from './dependencyTracker'
import * as runtimeStandards from './runtimeStandards'

const projectFromDtRow = (row) => {
  const latest = row?.latestVersion && typeof row.latestVersion === 'object' ? row.latestVersion : {}
  const severityCounts = toSeverityCounts(latest.severityCounts)
  return {
    projectName: row?.projectName || '',
    missingInDt: false,
    versionId: latest.versionId ?? null,
    version: latest.version ?? '',
    lastBomImport: latest.lastBomImport ?? null,
    inheritedRiskScore: Number(latest.inheritedRiskScore) || 0,
    totalFindings: Number(latest.totalFindings) || 0,
    severityCounts,
    availableVersions: Array.isArray(row?.availableVersions) ? row.availableVersions : [],
    sources: []
  }
}

const missingDtProject = (projectName) => ({
  projectName: projectName || '',
  missingInDt: true,
  versionId: null,
  version: '',
  lastBomImport: null,
  inheritedRiskScore: 0,
  totalFindings: 0,
  severityCounts: emptySeverityCounts(),
  availableVersions: [],
  sources: []
})

const normalizeRuntime = (raw) => {
  if (!raw || typeof raw !== 'object') return null
  const projectName = typeof raw.projectName === 'string' ? raw.projectName.trim() : ''
  if (!projectName) return null
  return {
    projectName,
    sourcePath: typeof raw.sourcePath === 'string' ? raw.sourcePath.trim() : '',
    runtime: typeof raw.runtime === 'string' ? raw.runtime.trim() : '',
    kind: typeof raw.kind === 'string' ? raw.kind.trim() : '',
    branch: typeof raw.branch === 'string'
      ? raw.branch.trim()
      : (typeof raw.sourceBranch === 'string' ? raw.sourceBranch.trim() : ''),
    assessment: runtimeStandards.assessRuntime({
      runtime: typeof raw.runtime === 'string' ? raw.runtime.trim() : '',
      kind: typeof raw.kind === 'string' ? raw.kind.trim() : ''
    })
  }
}

/**
 * Join SOCR clients/tree (color, vulnTracking, upgradeTracking) + code-tree + DT severity.
 * @param {'vulnerabilities'|'upgrades'} [mode='vulnerabilities']
 *   vulnerabilities → clients with vulnTracking
 *   upgrades → clients with upgradeTracking
 */
export const buildSocrVulnBoardViewModel = ({
  clientTree = [],
  codeTree = [],
  dtProjects = [],
  mode = 'vulnerabilities'
} = {}) => {
  const dtByKey = new Map()
  ;(Array.isArray(dtProjects) ? dtProjects : []).forEach((row) => {
    if (!row || typeof row !== 'object') return
    const key = normalizeProjectKey(row.projectName)
    if (!key) return
    dtByKey.set(key, projectFromDtRow(row))
  })

  const metaById = new Map()
  const metaByLabel = new Map()
  ;(Array.isArray(clientTree) ? clientTree : []).forEach((row) => {
    if (!row || typeof row !== 'object') return
    if (row.isActiveSupport === false) return
    const id = typeof row.id === 'string' ? row.id.trim() : ''
    const label = typeof row.label === 'string' ? row.label.trim() : ''
    if (!label) return
    const meta = {
      id,
      label,
      color: typeof row.color === 'string' && row.color.trim() ? row.color.trim() : '#6b7280',
      vulnTracking: Boolean(row.vulnTracking),
      upgradeTracking: Boolean(row.upgradeTracking)
    }
    if (id) metaById.set(id, meta)
    metaByLabel.set(label, meta)
  })

  const codeById = new Map()
  ;(Array.isArray(codeTree) ? codeTree : []).forEach((row) => {
    if (!row || typeof row !== 'object') return
    const id = typeof row.id === 'string' ? row.id.trim() : ''
    if (id) codeById.set(id, row)
  })

  const clientIds = new Set([
    ...metaById.keys(),
    ...codeById.keys()
  ])

  const tracked = []
  const untracked = []

  clientIds.forEach((id) => {
    const code = codeById.get(id)
    const meta = metaById.get(id) || metaByLabel.get(code?.label) || {
      id,
      label: code?.label || id,
      color: '#6b7280',
      vulnTracking: false,
      upgradeTracking: false
    }

    const reposRaw = Array.isArray(code?.repositories) ? code.repositories : []
    const repositories = reposRaw
      .filter((r) => r && typeof r === 'object')
      .map((repo) => {
        const runtimes = (Array.isArray(repo.projectRuntimes) ? repo.projectRuntimes : [])
          .map(normalizeRuntime)
          .filter(Boolean)
        const runtimesByKey = new Map()
        runtimes.forEach((rt) => {
          const key = normalizeProjectKey(rt.projectName)
          if (!key) return
          const list = runtimesByKey.get(key) || []
          list.push(rt)
          runtimesByKey.set(key, list)
        })

        const dtNames = Array.isArray(repo.dtProjects) ? repo.dtProjects : []
        const projects = dtNames
          .map((p) => {
            const name = typeof p?.projectName === 'string' ? p.projectName.trim() : ''
            if (!name) return null
            const key = normalizeProjectKey(name)
            const dt = dtByKey.get(key)
            const sources = runtimesByKey.get(key) || []
            return dt
              ? { ...dt, sources }
              : { ...missingDtProject(name), sources }
          })
          .filter(Boolean)
          .sort(compareTriage)

        const severityCounts = projects.reduce(
          (acc, p) => addSeverityCounts(acc, p.severityCounts),
          emptySeverityCounts()
        )
        const inheritedRiskScore = projects.reduce(
          (max, p) => Math.max(max, Number(p.inheritedRiskScore) || 0),
          0
        )
        const totalFindings = projects.reduce(
          (sum, p) => sum + (Number(p.totalFindings) || 0),
          0
        )

        return {
          repositoryUuid: repo.repositoryUuid || '',
          repositoryName: repo.repositoryName || '',
          repositorySlug: repo.repositorySlug || '',
          projectRuntimes: runtimes,
          projects,
          severityCounts,
          inheritedRiskScore,
          totalFindings
        }
      })
      .sort(compareTriage)

    const severityCounts = repositories.reduce(
      (acc, r) => addSeverityCounts(acc, r.severityCounts),
      emptySeverityCounts()
    )
    const inheritedRiskScore = repositories.reduce(
      (max, r) => Math.max(max, Number(r.inheritedRiskScore) || 0),
      0
    )
    const totalFindings = repositories.reduce(
      (sum, r) => sum + (Number(r.totalFindings) || 0),
      0
    )
    const dtProjectCount = repositories.reduce((n, r) => n + r.projects.length, 0)
    const allRuntimes = repositories.flatMap((repo) => repo.projectRuntimes || [])
    const runtimes = runtimeStandards.summarizeClientRuntimes(allRuntimes)
    const upgradeFindings = runtimeStandards.collectUpgradeFindings({
      clientLabel: meta.label,
      repositories
    })
    const needsUpgrade = upgradeFindings.some((f) => f.status === 'behind')
    const hasDt = dtProjectCount > 0
    const lastBomImport = latestBomImport({ repositories })
    const isTracked = mode === 'upgrades'
      ? Boolean(meta.upgradeTracking)
      : Boolean(meta.vulnTracking)

    const card = {
      id: meta.id || id,
      label: meta.label,
      color: meta.color,
      vulnTracking: meta.vulnTracking,
      upgradeTracking: meta.upgradeTracking,
      repositories,
      severityCounts,
      inheritedRiskScore,
      totalFindings,
      repoCount: repositories.length,
      dtProjectCount,
      hasDt,
      lastBomImport,
      runtimes,
      needsUpgrade,
      upgradeFindings
    }

    if (isTracked) tracked.push(card)
    else {
      untracked.push({
        id: card.id,
        label: card.label,
        color: card.color,
        repoCount: card.repoCount,
        hasDt: card.hasDt,
        runtimes: card.runtimes,
        needsUpgrade: card.needsUpgrade
      })
    }
  })

  tracked.sort((a, b) => {
    const triage = compareTriage(a, b)
    if (triage !== 0) return triage
    return String(a.label).localeCompare(String(b.label))
  })
  untracked.sort((a, b) => String(a.label).localeCompare(String(b.label)))

  const boardSeverity = tracked.reduce(
    (acc, c) => addSeverityCounts(acc, c.severityCounts),
    emptySeverityCounts()
  )
  const withDtCount = tracked.filter((c) => c.hasDt).length
  const withoutDtCount = tracked.length - withDtCount
  const needsUpgradeCount = tracked.filter((c) => c.needsUpgrade).length

  return {
    tracked,
    untracked,
    boardSeverity,
    trackedCount: tracked.length,
    untrackedCount: untracked.length,
    withDtCount,
    withoutDtCount,
    needsUpgradeCount
  }
}

const SEVERITY_EXPORT_ORDER = ['critical', 'high', 'medium', 'low']

const formatBomVersionDate = (value) => {
  if (value == null || value === '') return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}.${m}.${d}`
}

/** Most recent BOM import across a client's DT projects (Date or null). */
export const latestBomImport = (client) => {
  let latest = null
  ;(client?.repositories || []).forEach((repo) => {
    ;(repo?.projects || []).forEach((project) => {
      const raw = project?.lastBomImport
      if (raw == null || raw === '') return
      const date = raw instanceof Date ? raw : new Date(raw)
      if (Number.isNaN(date.getTime())) return
      if (!latest || date > latest) latest = date
    })
  })
  return latest
}

/** Human-facing scan label, e.g. "Scanned Sep 10, 2026". */
export const formatLastScannedLabel = (value) => {
  if (value == null || value === '') return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return `Scanned ${date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })}`
}

/**
 * Build plain-text client vulnerability summary for export.
 * @param {object[]} clients
 * @param {object} options
 * @param {string[]} options.severities - subset of critical|high|medium|low
 * @param {boolean} [options.includeVersion=true]
 */
export const buildVulnSummaryExport = (clients = [], {
  severities = ['critical', 'high', 'medium'],
  includeVersion = true
} = {}) => {
  const enabled = new Set(
    (Array.isArray(severities) ? severities : [])
      .map((s) => String(s || '').toLowerCase())
      .filter((s) => SEVERITY_EXPORT_ORDER.includes(s))
  )
  const ordered = SEVERITY_EXPORT_ORDER.filter((s) => enabled.has(s))

  const blocks = (Array.isArray(clients) ? clients : []).map((client) => {
    const sev = client?.severityCounts || {}
    const parts = ordered
      .map((key) => {
        const n = Number(sev[key]) || 0
        if (n <= 0) return null
        return `${n.toLocaleString()} ${key}`
      })
      .filter(Boolean)

    const lines = [client?.label || '—']
    if (parts.length) lines.push(parts.join(', '))
    lines.push(`Number of projects: ${Number(client?.dtProjectCount) || 0}`)

    if (includeVersion) {
      const version = formatBomVersionDate(latestBomImport(client))
      if (version) lines.push(`Version: ${version}`)
    }

    return lines.join('\n')
  })

  return blocks.join('\n\n')
}

const normalizeExportSeverity = (value) => String(value || '').trim().toLowerCase()

const formatFindingPackage = (finding, format = 'md') => {
  const name = String(finding?.packageName || '').trim()
  const version = String(finding?.version || '').trim()
  const latest = String(finding?.latestVersion || '').trim()
  if (!name) return ''
  let pkg = name
  if (version && latest && latest !== version) pkg = `${name}@${version} → ${latest}`
  else if (version) pkg = `${name}@${version}`
  return format === 'md' ? `\`${pkg}\`` : pkg
}

/**
 * Collect DT project targets for complex export (per client + versionId).
 * @param {object[]} clients
 */
export const collectVulnExportTargets = (clients = []) => {
  const seen = new Set()
  const targets = []

  ;(Array.isArray(clients) ? clients : []).forEach((client) => {
    const clientKey = client?.id || client?.label || ''
    ;(client?.repositories || []).forEach((repo) => {
      ;(repo?.projects || []).forEach((project) => {
        if (!project || project.missingInDt) return
        const versionId = typeof project.versionId === 'string' ? project.versionId.trim() : ''
        if (!versionId) return
        const dedupe = `${clientKey}::${versionId}`
        if (seen.has(dedupe)) return
        seen.add(dedupe)
        targets.push({
          versionId,
          clientId: client.id || '',
          clientLabel: client.label || '—',
          repositoryName: repo.repositoryName || repo.repositorySlug || '',
          repositorySlug: repo.repositorySlug || '',
          projectName: project.projectName || '',
          version: project.version || '',
          inheritedRiskScore: Number(project.inheritedRiskScore) || 0,
          totalFindings: Number(project.totalFindings) || 0,
          lastBomImport: project.lastBomImport || null
        })
      })
    })
  })

  return targets
}

/**
 * Complex vulnerability summary (markdown or plain text).
 * @param {object[]} targets - from collectVulnExportTargets
 * @param {Record<string, object>} detailsByVersionId - GET /api/projects/{versionId} payloads
 * @param {object} options
 * @param {'md'|'txt'} [options.format='md']
 */
export const buildVulnComplexExport = (targets = [], detailsByVersionId = {}, {
  severities = ['critical', 'high', 'medium'],
  includeVersion = true,
  format = 'md'
} = {}) => {
  const asMd = format !== 'txt'
  const enabled = new Set(
    (Array.isArray(severities) ? severities : [])
      .map((s) => normalizeExportSeverity(s))
      .filter((s) => SEVERITY_EXPORT_ORDER.includes(s))
  )
  const ordered = SEVERITY_EXPORT_ORDER.filter((s) => enabled.has(s))
  const detailMap = detailsByVersionId && typeof detailsByVersionId === 'object'
    ? detailsByVersionId
    : {}

  const metaLine = (label, value) => (
    asMd ? `- **${label}:** ${value}` : `${label}: ${value}`
  )
  const heading = (text, level = 1) => {
    if (!asMd) return text
    return `${'#'.repeat(level)} ${text}`
  }
  const note = (text) => (asMd ? `_${text}_` : text)

  const blocks = (Array.isArray(targets) ? targets : []).map((target) => {
    const detail = detailMap[target.versionId]
    const projectName = detail?.projectName || target.projectName || '—'
    const versionLabel = detail?.version || target.version || ''
    const risk = detail?.inheritedRiskScore ?? target.inheritedRiskScore
    const findings = Array.isArray(detail?.findings) ? detail.findings : []

    const bySeverity = ordered.reduce((acc, key) => {
      acc[key] = findings.filter((f) => normalizeExportSeverity(f?.severity) === key)
      return acc
    }, {})

    const filteredCount = ordered.reduce((sum, key) => sum + (bySeverity[key]?.length || 0), 0)
    if (!detail && !target.projectName) return null

    const lines = [heading(projectName, 1)]
    if (target.clientLabel) lines.push(metaLine('Client', target.clientLabel))
    if (target.repositoryName) lines.push(metaLine('Repository', target.repositoryName))

    if (includeVersion) {
      const bomDate = formatBomVersionDate(detail?.lastBomImport || target.lastBomImport)
      const shown = versionLabel || bomDate
      if (shown) lines.push(metaLine('Version', shown))
    }

    if (risk != null && risk !== '') {
      const riskNum = Number(risk)
      if (!Number.isNaN(riskNum)) lines.push(metaLine('Risk score', riskNum))
    }

    const totalFromDetail = Array.isArray(detail?.findings)
      ? detail.findings.length
      : Number(detail?.severityCounts?.total)
    const findingsLabel = Number.isFinite(totalFromDetail) && totalFromDetail >= 0
      ? totalFromDetail
      : (Number(target.totalFindings) || findings.length)
    const findingsValue = filteredCount !== findingsLabel
      ? `${findingsLabel} (${filteredCount} in selected severities)`
      : String(findingsLabel)
    lines.push(metaLine('Findings', findingsValue))

    if (!detail) {
      lines.push('')
      lines.push(note('Could not load finding details for this project.'))
      return lines.join('\n')
    }

    if (filteredCount === 0) {
      lines.push('')
      lines.push(note('No findings in the selected severities.'))
      return lines.join('\n')
    }

    ordered.forEach((key) => {
      const list = bySeverity[key] || []
      if (!list.length) return
      lines.push('')
      lines.push(heading(`${key.toUpperCase()} (${list.length})`, 2))
      lines.push('')
      list.forEach((finding) => {
        const vulnId = String(finding?.vulnerabilityId || '').trim() || '—'
        const pkg = formatFindingPackage(finding, asMd ? 'md' : 'txt')
        if (asMd) {
          lines.push(pkg ? `- **${vulnId}** ${pkg}` : `- **${vulnId}**`)
        } else {
          lines.push(pkg ? `- ${vulnId}  ${pkg}` : `- ${vulnId}`)
        }
        const desc = String(finding?.description || '').trim()
        if (desc) lines.push(asMd ? `  - ${desc}` : `  ${desc}`)
      })
    })

    return lines.join('\n')
  }).filter(Boolean)

  return blocks.join('\n\n')
}
